import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  Badge,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { Copy, MousePointerClick, UserPlus, Target, Coins, ArrowRight } from 'lucide-react';
import {
  useAffiliateProfile,
  useAffiliateStats,
  formatCurrency,
  statusTone,
  type AffiliateProfile,
} from '@/api/affiliate';
import { RegisterAffiliate } from './RegisterAffiliate';
import styles from './Affiliate.module.css';

const TIER_TONE: Record<string, 'neutral' | 'primary' | 'accent' | 'success' | 'warning'> = {
  bronze: 'warning',
  silver: 'neutral',
  gold: 'warning',
  platinum: 'primary',
  diamond: 'accent',
  standard: 'primary',
};

export function AffiliateDashboard() {
  const navigate = useNavigate();
  const { show } = useToast();
  const { data: profile, isLoading, isError, refetch } = useAffiliateProfile();
  const hasProfile = !!profile;
  const { data: stats } = useAffiliateStats(hasProfile);

  if (isLoading) return <Skeleton style={{ height: 320 }} />;
  // A load failure (500) must not masquerade as "not joined" — the hook maps only 404 to null.
  if (isError) {
    return (
      <EmptyState
        title="Could not load affiliate profile"
        description="Something went wrong. Please try again."
        actions={<Button variant="secondary" onClick={() => refetch()}>Retry</Button>}
      />
    );
  }
  if (!profile) return <RegisterAffiliate />;

  const conversionRate = stats?.conversion_rate ?? 0;
  const unpaid = stats?.pending_commission ?? profile.unpaid_commission;

  return (
    <>
      <Card>
        <CardHeader
          actions={
            <Badge tone={statusTone(profile.status)} appearance="subtle">
              {profile.status}
            </Badge>
          }
        >
          <div className={styles.profileHead}>
            <CardTitle>{profile.referrer_name || 'Affiliate'}</CardTitle>
            <Badge tone={TIER_TONE[profile.tier?.toLowerCase()] ?? 'neutral'}>
              {profile.tier?.toUpperCase()}
            </Badge>
            <span className={styles.code}>{profile.referral_code}</span>
          </div>
          <CardDescription>
            {profile.commission_percent}% commission · joined affiliate program
          </CardDescription>
        </CardHeader>
        <CardBody>
          <ReferralLink
            profile={profile}
            onCopy={() => show({ tone: 'success', title: 'Referral link copied' })}
            onError={() =>
              show({ tone: 'warning', title: 'Copy failed — select the link and copy it manually' })
            }
          />

          <div className={styles.highlights} style={{ marginTop: 'var(--vn-space-5)' }}>
            <div className={styles.highlight}>
              <span className={styles.highlightLabel}>Total revenue</span>
              <span className={styles.highlightValue}>{formatCurrency(profile.total_revenue)}</span>
            </div>
            <div className={styles.highlight}>
              <span className={styles.highlightLabel}>Total commission</span>
              <span className={styles.highlightValue}>{formatCurrency(profile.total_commission)}</span>
            </div>
            <div className={styles.highlight}>
              <span className={styles.highlightLabel}>Unpaid commission</span>
              <span className={styles.highlightValue}>{formatCurrency(unpaid)}</span>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className={styles.stats}>
        <Stat
          icon={<MousePointerClick size={15} />}
          label="Total clicks"
          value={(stats?.total_clicks ?? profile.total_clicks).toLocaleString()}
        />
        <Stat
          icon={<UserPlus size={15} />}
          label="Signups"
          value={(stats?.total_signups ?? profile.total_signups).toLocaleString()}
        />
        <Stat
          icon={<Target size={15} />}
          label="Conversions"
          value={(stats?.total_conversions ?? profile.total_conversions).toLocaleString()}
          sub={`${conversionRate.toFixed(1)}% rate`}
        />
        <Stat
          icon={<Coins size={15} />}
          label="Commission"
          value={formatCurrency(stats?.total_commission ?? profile.total_commission)}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Payout settings</CardTitle>
          <CardDescription>Where and when your commission is paid out.</CardDescription>
        </CardHeader>
        <CardBody>
          <div className={styles.payoutGrid}>
            <div className={styles.payoutItem}>
              <span className={styles.payoutLabel}>Payout threshold</span>
              <span className={styles.payoutValue}>{formatCurrency(profile.payout_threshold)}</span>
            </div>
            <div className={styles.payoutItem}>
              <span className={styles.payoutLabel}>Commission rate</span>
              <span className={styles.payoutValue}>{profile.commission_percent}%</span>
            </div>
            <div className={styles.payoutItem}>
              <span className={styles.payoutLabel}>Payout method</span>
              <span className={styles.payoutValue}>
                {profile.payout_method ? profile.payout_method : 'Not set'}
              </span>
            </div>
            <div className={styles.payoutItem}>
              <span className={styles.payoutLabel}>Available to withdraw</span>
              <span className={styles.payoutValue}>{formatCurrency(unpaid)}</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)', marginTop: 'var(--vn-space-5)' }}>
            <Button variant="secondary" size="sm" onClick={() => navigate('analytics')}>
              View analytics <ArrowRight size={14} />
            </Button>
            <Button variant="secondary" size="sm" onClick={() => navigate('payouts')}>
              Payouts <ArrowRight size={14} />
            </Button>
            <Button variant="secondary" size="sm" onClick={() => navigate('settings')}>
              Payout settings <ArrowRight size={14} />
            </Button>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

function ReferralLink({
  profile,
  onCopy,
  onError,
}: {
  profile: AffiliateProfile;
  onCopy: () => void;
  onError: () => void;
}) {
  async function copy() {
    try {
      await navigator.clipboard.writeText(profile.referral_link);
      onCopy();
    } catch {
      onError();
    }
  }
  return (
    <div className={styles.referral}>
      <span className={styles.referralUrl}>{profile.referral_link}</span>
      <Button variant="secondary" size="sm" onClick={copy}>
        <Copy size={14} /> Copy
      </Button>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className={styles.stat}>
      <span className={styles.statLabel}>
        {icon} {label}
      </span>
      <span className={styles.statValue}>{value}</span>
      {sub && <span className={styles.statSub}>{sub}</span>}
    </div>
  );
}
